/** Bell icon + unread badge + dropdown inbox. */
import { useCallback, useEffect, useRef, useState } from "react";
import * as api from "../../api/notifications";
import type { AppNotification } from "../../api/types";
import { Badge, EmptyState } from "../../components/ui";
import { useT } from "../../i18n";

const SEVERITY_TONE = {
  info: "brand",
  success: "success",
  warning: "warning",
  critical: "danger",
} as const;

export function NotificationsBell() {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState<AppNotification[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  const refreshCount = useCallback(async () => {
    try {
      const res = await api.unreadCount();
      setCount(res.count);
    } catch {
      // Bell is decorative — a failed count never breaks the shell.
    }
  }, []);

  useEffect(() => {
    void refreshCount();
  }, [refreshCount]);

  // Close on outside click while open.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      try {
        setItems(await api.listNotifications());
      } catch {
        setItems([]);
      }
    }
  };

  const onMarkAll = async () => {
    await api.markAllRead();
    setItems((prev) => prev.map((n) => ({ ...n, read_at: n.read_at ?? "now" })));
    setCount(0);
  };

  const onItemClick = async (n: AppNotification) => {
    if (!n.read_at) {
      await api.markRead(n.id);
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read_at: "now" } : x)));
      setCount((c) => Math.max(0, c - 1));
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        aria-label={t("nav.notifications")}
        onClick={toggle}
        className="relative rounded-full p-2 text-muted hover:bg-canvas hover:text-ink"
      >
        <span aria-hidden>🔔</span>
        {count > 0 && (
          <span
            data-testid="unread-badge"
            className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-danger px-1 text-[10px] font-bold text-white"
          >
            {count}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-11 z-40 w-80 rounded-card border border-line bg-surface p-3 shadow-xl">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-bold">{t("notifications.title")}</h3>
            <button onClick={onMarkAll} className="text-xs font-medium text-brand hover:underline">
              {t("notifications.markAll")}
            </button>
          </div>
          {items.length === 0 ? (
            <EmptyState message={t("notifications.empty")} />
          ) : (
            <ul className="max-h-96 space-y-1 overflow-y-auto">
              {items.map((n) => (
                <li key={n.id}>
                  <button
                    onClick={() => onItemClick(n)}
                    className={`w-full rounded-control p-2 text-left hover:bg-canvas ${n.read_at ? "opacity-60" : ""}`}
                  >
                    <div className="flex items-center gap-2">
                      <Badge tone={SEVERITY_TONE[n.severity]}>{n.severity}</Badge>
                      <span className="text-sm font-semibold text-ink">{n.title}</span>
                    </div>
                    {n.body && <p className="mt-1 text-xs text-muted">{n.body}</p>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
